import styled, { css } from 'styled-components'

export interface FilterPanelContainerProps {
  $collapsed?: boolean
  $swipeOffset?: number
}

export interface FilterToggleProps {
  $collapsed?: boolean
}

export const Container = styled.aside.withConfig({
  shouldForwardProp: (prop) => !prop.startsWith('$'),
})<FilterPanelContainerProps>`
  width: 280px;
  background: ${({ theme }) => theme.colors.background.primary};
  border-right: 1px solid ${({ theme }) => theme.colors.border.primary};
  display: flex;
  flex-direction: column;
  transition: margin-left 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  z-index: 100;
  position: relative;
  height: 100%;

  ${({ $collapsed }) =>
    $collapsed &&
    css`
      margin-left: -280px;
    `}

  /* Mobile: Bottom drawer pattern */
  @media (max-width: 768px) {
    position: fixed;
    bottom: 0;
    left: 0;
    right: 0;
    width: 100%;
    height: calc(100dvh - var(--header-height, 48px));
    max-height: calc(100dvh - var(--header-height, 48px));
    border-right: none;
    border-top: 1px solid ${({ theme }) => theme.colors.border.primary};
    border-radius: ${({ theme }) => theme.borderRadius.large}
      ${({ theme }) => theme.borderRadius.large} 0 0;
    box-shadow: ${({ theme }) => theme.shadows.lg};
    z-index: 300;
    margin-left: 0;
    transform: translateY(
      ${({ $collapsed, $swipeOffset }) =>
        $collapsed ? '100%' : `${$swipeOffset || 0}px`}
    );
    transition: transform 0.3s cubic-bezier(0.4, 0, 0.2, 1);
    padding-bottom: env(safe-area-inset-bottom, 0);
  }
`

/** Drag handle for mobile bottom drawer */
export const DragHandle = styled.div`
  display: none;

  @media (max-width: 768px) {
    display: flex;
    justify-content: center;
    align-items: center;
    padding: 12px 0 8px 0;
    cursor: grab;
    touch-action: none;

    &:active {
      cursor: grabbing;
    }

    /* The visual handle indicator */
    &::after {
      content: '';
      width: 40px;
      height: 4px;
      background: ${({ theme }) => theme.colors.border.primary};
      border-radius: 2px;
    }
  }
`

export const FilterHeader = styled.div`
  padding: 16px;
  border-bottom: 1px solid ${({ theme }) => theme.colors.border.primary};
  display: flex;
  align-items: center;
  justify-content: space-between;
`

export const FilterTitle = styled.h2`
  font-size: 13px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.text.primary};
  margin: 0;
`

export const FilterContent = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 16px;
`

export const FilterToggle = styled.button.withConfig({
  shouldForwardProp: (prop) => !prop.startsWith('$'),
})<FilterToggleProps>`
  position: absolute;
  right: -26px;
  top: 50%;
  transform: translateY(-50%);
  width: 26px;
  height: 48px;
  background: ${({ theme }) => theme.colors.background.primary};
  border: 1px solid ${({ theme }) => theme.colors.border.primary};
  border-left: none;
  border-radius: 0 ${({ theme }) => theme.borderRadius.medium}
    ${({ theme }) => theme.borderRadius.medium} 0;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${({ theme }) => theme.colors.text.secondary};
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  z-index: 101;

  &:hover {
    color: ${({ theme }) => theme.colors.text.primary};
  }

  &:focus {
    outline: none;
    box-shadow: ${({ theme }) => theme.shadows.focus};
  }

  .icon {
    width: 13px;
    height: 13px;
    stroke: currentColor;
    stroke-width: 2;
    fill: none;
    transform: rotate(180deg);
    transition: transform 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  }

  ${({ $collapsed }) =>
    $collapsed &&
    css`
      .icon {
        transform: rotate(0deg);
      }
    `}

  /* Mobile: Larger touch target on tablet */
  @media (max-width: 1023px) and (min-width: 769px) {
    width: ${({ theme }) => theme.touchTargets.minimum};
    height: 72px;
    right: -${({ theme }) => theme.touchTargets.minimum};
  }

  /* Mobile: Hide toggle button, use bottom drawer instead */
  @media (max-width: 768px) {
    display: none;
  }

  /* Disable hover effects on touch devices */
  @media (hover: none) {
    &:hover {
      color: ${({ theme }) => theme.colors.text.secondary};
    }

    &:active {
      color: ${({ theme }) => theme.colors.text.primary};
    }
  }
`

export const MobileOverlay = styled.div<{ $visible: boolean }>`
  display: none;

  @media (max-width: 768px) {
    display: block;
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(0, 0, 0, 0.5);
    z-index: 150;
    opacity: ${({ $visible }) => ($visible ? 1 : 0)};
    visibility: ${({ $visible }) => ($visible ? 'visible' : 'hidden')};
    transition:
      opacity 0.3s ease-out,
      visibility 0.3s ease-out;
  }
`

export const ResetButton = styled.button`
  background: none;
  border: none;
  color: ${({ theme }) => theme.colors.text.secondary};
  cursor: pointer;
  padding: 4px;
  border-radius: ${({ theme }) => theme.borderRadius.small};
  transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
  display: flex;
  align-items: center;
  justify-content: center;
  /* Minimum touch target */
  min-width: 26px;
  min-height: 26px;

  &:hover {
    color: ${({ theme }) => theme.colors.text.primary};
    background: ${({ theme }) => theme.colors.background.tertiary};
  }

  &:focus {
    outline: none;
    box-shadow: ${({ theme }) => theme.shadows.focus};
  }

  .icon {
    width: 13px;
    height: 13px;
    stroke: currentColor;
    stroke-width: 2;
    fill: none;
  }

  /* Mobile: Ensure 44px minimum touch target */
  @media (max-width: 1023px) {
    min-width: ${({ theme }) => theme.touchTargets.minimum};
    min-height: ${({ theme }) => theme.touchTargets.minimum};
  }

  /* Disable hover effects on touch devices */
  @media (hover: none) {
    &:hover {
      color: ${({ theme }) => theme.colors.text.secondary};
      background: none;
    }

    &:active {
      color: ${({ theme }) => theme.colors.text.primary};
      background: ${({ theme }) => theme.colors.background.tertiary};
    }
  }
`
